/**
 * usePushNotifications — manages push subscription lifecycle.
 * Registers/unregisters with the backend when subscription changes.
 */
import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const VAPID_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return new Uint8Array([...raw].map((c) => c.charCodeAt(0)));
}

export function usePushNotifications() {
  const [permission, setPermission] = useState(Notification?.permission || 'default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  // Check current subscription state on mount
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    navigator.serviceWorker.ready.then(async (reg) => {
      const sub = await reg.pushManager.getSubscription();
      setIsSubscribed(!!sub);
    });
  }, []);

  const subscribe = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return { success: false, reason: 'not_supported' };
    }
    if (!VAPID_KEY) {
      return { success: false, reason: 'no_vapid_key' };
    }

    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;

      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setLoading(false);
        return { success: false, reason: 'permission_denied' };
      }

      // Create subscription
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_KEY),
      });

      // Send to backend
      await api.post('/push/subscribe', { subscription: sub.toJSON() });
      setIsSubscribed(true);
      setLoading(false);
      return { success: true };
    } catch (err) {
      setLoading(false);
      return { success: false, reason: err.message };
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    if (!('serviceWorker' in navigator)) return;
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.delete('/push/subscribe', { data: { endpoint: sub.endpoint } });
        await sub.unsubscribe();
        setIsSubscribed(false);
      }
    } catch (err) {
      console.warn('[Push] Unsubscribe failed:', err);
    }
    setLoading(false);
  }, []);

  return {
    isSupported: 'PushManager' in window && 'serviceWorker' in navigator,
    permission,
    isSubscribed,
    loading,
    subscribe,
    unsubscribe,
  };
}
